import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

export type AuthAppearance = 'control' | 'clarity';
type PageLocation = { pathname: string; search?: string };
const STORAGE_KEY = 'auth:last_page_appearance';
let lastPage: string | null = null;
const pageKey = ({ pathname, search = '' }: PageLocation): string => `${pathname}${search}`;

/** Presentation receipt for the page actually rendered, never an experiment assignment. */
export function rememberAuthAppearance(location: PageLocation, appearance: AuthAppearance): void {
  lastPage = `${appearance}:${pageKey(location)}`;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, lastPage);
  } catch {
    // Keep same-tab navigation working when browser storage is unavailable.
  }
}

export function getAuthAppearance(location?: PageLocation | null): AuthAppearance {
  if (!location) return 'control';
  if (/^\/(home-clarity|courses-clarity|challenges-clarity)(\/|$)/.test(location.pathname)) {
    return 'clarity';
  }
  let receipt = lastPage;
  try {
    receipt = window.sessionStorage.getItem(STORAGE_KEY) || lastPage;
  } catch {
    // An unavailable or invalid receipt must not affect authentication.
  }
  return receipt === `clarity:${pageKey(location)}` ? 'clarity' : 'control';
}

export function usePageAuthAppearance(appearance: AuthAppearance): void {
  const { pathname, search } = useLocation();
  useLayoutEffect(() => {
    // Reconstructed backdrops must not replace the receipt while a form is open.
    if (/^\/(sales|login|checkout)(\/|$)/.test(window.location.pathname)) return;
    rememberAuthAppearance({ pathname, search }, appearance);
  }, [appearance, pathname, search]);
}
